/**
 * The performance calendar: an April-to-March financial year split into four
 * quarters, each with a self-assessment window at its tail.
 *
 * Every rule here is derived, never stored, so a quarter's window cannot drift
 * out of sync with its dates. The one exception is the HR override, which is
 * stored per quarter and layered on top by `selfAssessmentState`.
 */

import {
  addDays,
  isWithin,
  lastDayOfMonth,
  plainDate,
  plainDateParts,
  today,
  type PlainDate,
} from './date';
import type { WindowOverride } from './enums';

/** The financial year starts on 1 April. */
export const FY_START_MONTH = 4;

/** Self-assessment opens for the last 7 days of the quarter (Q1: 24-30 Jun). */
export const SELF_ASSESSMENT_WINDOW_DAYS = 7;

/** A manager gets 3 days to review, counted from that employee's submission. */
export const MANAGER_REVIEW_DAYS = 3;

/** The annual review runs in the last 15 days of March (17-31 Mar). */
export const ANNUAL_REVIEW_WINDOW_DAYS = 15;

export const QUARTER_INDEXES = [1, 2, 3, 4] as const;
export type QuarterIndex = (typeof QUARTER_INDEXES)[number];

export function isQuarterIndex(value: number): value is QuarterIndex {
  return value === 1 || value === 2 || value === 3 || value === 4;
}

/** First calendar month of each quarter, in the FY's own ordering. */
const QUARTER_START_MONTH: Record<QuarterIndex, number> = { 1: 4, 2: 7, 3: 10, 4: 1 };
const QUARTER_END_MONTH: Record<QuarterIndex, number> = { 1: 6, 2: 9, 3: 12, 4: 3 };

export const QUARTER_MONTH_LABELS: Record<QuarterIndex, string> = {
  1: 'Apr - Jun',
  2: 'Jul - Sep',
  3: 'Oct - Dec',
  4: 'Jan - Mar',
};

/**
 * The year the financial year *starts* in. Jan-Mar belong to the FY that began
 * the previous April, which is the single most common off-by-one in this domain.
 */
export function fiscalYearStartYear(date: PlainDate): number {
  const { year, month } = plainDateParts(date);
  return month >= FY_START_MONTH ? year : year - 1;
}

/** e.g. 2026 -> "FY2026-27" */
export function fiscalYearLabel(fyStartYear: number): string {
  const endShort = String((fyStartYear + 1) % 100).padStart(2, '0');
  return `FY${fyStartYear}-${endShort}`;
}

export function fiscalYearBounds(fyStartYear: number): { startsOn: PlainDate; endsOn: PlainDate } {
  return {
    startsOn: plainDate(fyStartYear, FY_START_MONTH, 1),
    endsOn: plainDate(fyStartYear + 1, 3, 31),
  };
}

export function quarterIndexForDate(date: PlainDate): QuarterIndex {
  const { month } = plainDateParts(date);
  if (month >= 4 && month <= 6) return 1;
  if (month >= 7 && month <= 9) return 2;
  if (month >= 10 && month <= 12) return 3;
  return 4;
}

/** Q4 spans into the next calendar year, so its months sit on fyStartYear + 1. */
export function quarterBounds(
  fyStartYear: number,
  index: QuarterIndex,
): { startsOn: PlainDate; endsOn: PlainDate } {
  const calendarYear = index === 4 ? fyStartYear + 1 : fyStartYear;
  const startMonth = QUARTER_START_MONTH[index];
  const endMonth = QUARTER_END_MONTH[index];
  return {
    startsOn: plainDate(calendarYear, startMonth, 1),
    endsOn: plainDate(calendarYear, endMonth, lastDayOfMonth(calendarYear, endMonth)),
  };
}

export interface QuarterDefinition {
  index: QuarterIndex;
  label: string;
  months: string;
  startsOn: PlainDate;
  endsOn: PlainDate;
  selfOpensOn: PlainDate;
  selfClosesOn: PlainDate;
}

/** The four quarters of a financial year. Used by the seed and by cycle creation. */
export function buildCycleQuarters(fyStartYear: number): QuarterDefinition[] {
  return QUARTER_INDEXES.map((index) => {
    const { startsOn, endsOn } = quarterBounds(fyStartYear, index);
    const window = calendarSelfAssessmentWindow(startsOn, endsOn);
    return {
      index,
      label: `Q${index}`,
      months: QUARTER_MONTH_LABELS[index],
      startsOn,
      endsOn,
      selfOpensOn: window.opensOn,
      selfClosesOn: window.closesOn,
    };
  });
}

/**
 * The calendar window, ignoring any HR override: the final
 * SELF_ASSESSMENT_WINDOW_DAYS days of the quarter, inclusive of the last day.
 */
export function calendarSelfAssessmentWindow(
  startsOn: PlainDate,
  endsOn: PlainDate,
  windowDays: number = SELF_ASSESSMENT_WINDOW_DAYS,
): { opensOn: PlainDate; closesOn: PlainDate } {
  const opensOn = addDays(endsOn, -(windowDays - 1));
  // A quarter shorter than the window (only possible for a hand-made cycle)
  // must not open before it starts.
  return { opensOn: opensOn < startsOn ? startsOn : opensOn, closesOn: endsOn };
}

/** Why the window is in the state it is - drives the UI banner. */
export type WindowReason =
  | 'FROZEN'
  | 'FORCED_OPEN'
  | 'FORCED_CLOSED'
  | 'OPEN_ON_CALENDAR'
  | 'BEFORE_WINDOW'
  | 'AFTER_WINDOW';

export interface QuarterWindowInput {
  startsOn: PlainDate;
  endsOn: PlainDate;
  /**
   * The window HR configured for this quarter. Omitted falls back to the calendar
   * rule, so nothing that does not configure a window changes behaviour.
   *
   * Stored, unlike everything else here, precisely because it is a decision rather
   * than a derivation: re-deriving it would rewrite the window a past quarter
   * actually ran with the moment the rule or the configuration changed.
   */
  opensOn?: PlainDate;
  closesOn?: PlainDate;
  /** Stored per quarter; 'NONE' means follow the calendar. */
  override?: WindowOverride;
  /**
   * HR's final step. A frozen quarter is shut to every stage, so it outranks
   * the calendar AND a FORCED_OPEN override - otherwise a quarter frozen while an
   * override was still in place would keep reporting its window as open, and every
   * caller would have to remember to combine the two fields itself.
   */
  frozen?: boolean;
}

export interface SelfAssessmentState {
  open: boolean;
  reason: WindowReason;
  /** True when an HR override, not the calendar, decided this. */
  overridden: boolean;
  opensOn: PlainDate;
  closesOn: PlainDate;
  /** Days until the window opens, or null once it has opened or passed. */
  opensInDays: number | null;
  /** Days remaining including today, or null when not open. */
  closesInDays: number | null;
}

/**
 * Whether an employee may submit a self-assessment for this quarter today.
 *
 * The HR override wins over the calendar in both directions. This is what makes
 * the system demonstrable and correctable outside the 7-day window - without it
 * a missed quarter could never be reopened.
 */
export function selfAssessmentState(
  quarter: QuarterWindowInput,
  on: PlainDate = today(),
): SelfAssessmentState {
  const calendar = calendarSelfAssessmentWindow(quarter.startsOn, quarter.endsOn);
  const opensOn = quarter.opensOn ?? calendar.opensOn;
  const closesOn = quarter.closesOn ?? calendar.closesOn;
  const override = quarter.override ?? 'NONE';

  const base = { opensOn, closesOn };

  // Checked before the override: freeze is the later, stronger decision.
  if (quarter.frozen === true) {
    return { ...base, open: false, reason: 'FROZEN', overridden: false, opensInDays: null, closesInDays: null };
  }
  if (override === 'FORCED_OPEN') {
    return { ...base, open: true, reason: 'FORCED_OPEN', overridden: true, opensInDays: null, closesInDays: null };
  }
  if (override === 'FORCED_CLOSED') {
    return { ...base, open: false, reason: 'FORCED_CLOSED', overridden: true, opensInDays: null, closesInDays: null };
  }

  if (on < opensOn) {
    return {
      ...base,
      open: false,
      reason: 'BEFORE_WINDOW',
      overridden: false,
      opensInDays: daysUntil(on, opensOn),
      closesInDays: null,
    };
  }
  if (on > closesOn) {
    return { ...base, open: false, reason: 'AFTER_WINDOW', overridden: false, opensInDays: null, closesInDays: null };
  }
  return {
    ...base,
    open: true,
    reason: 'OPEN_ON_CALENDAR',
    overridden: false,
    opensInDays: null,
    closesInDays: daysUntil(on, closesOn) + 1,
  };
}

export function isSelfAssessmentOpen(quarter: QuarterWindowInput, on: PlainDate = today()): boolean {
  return selfAssessmentState(quarter, on).open;
}

/**
 * A per-employee reopen, granted either by a manager returning work for correction
 * or by HR reopening a closed assessment.
 *
 * A deadline instant rather than a PlainDate, and a boolean rather than a window
 * reason, because it is not a window: it grants one employee a way back in without
 * moving the quarter-wide dates for everybody else. Callers OR it into `windowOpen`
 * - without that, a manager who returns work after the window shut leaves the
 * employee holding an unlocked assessment they cannot resubmit.
 *
 * It never defeats a freeze. Frozen is checked first and separately, on the write
 * path, precisely so a reopen cannot reach past HR's final shutter.
 */
export function isReopenActive(reopenedUntil: Date | null | undefined, now: Date = new Date()): boolean {
  return reopenedUntil != null && reopenedUntil.getTime() > now.getTime();
}

function daysUntil(from: PlainDate, to: PlainDate): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * When this employee's manager review falls due: 3 days from *their* submission,
 * not from a quarter-wide date. Two employees submitting days apart get
 * different deadlines, which is what the spec's "manager gets 3 days" means.
 */
export function managerReviewDueAt(
  submittedAt: Date,
  days: number = MANAGER_REVIEW_DAYS,
): Date {
  return new Date(submittedAt.getTime() + days * 86_400_000);
}

export function isManagerReviewOverdue(dueAt: Date, now: Date = new Date()): boolean {
  return now.getTime() > dueAt.getTime();
}

/** Last 15 days of March: final rating, promotion and salary recommendations. */
export function annualReviewWindow(fyStartYear: number): { opensOn: PlainDate; closesOn: PlainDate } {
  const endsOn = plainDate(fyStartYear + 1, 3, 31);
  return { opensOn: addDays(endsOn, -(ANNUAL_REVIEW_WINDOW_DAYS - 1)), closesOn: endsOn };
}

export function isAnnualReviewOpen(fyStartYear: number, on: PlainDate = today()): boolean {
  const { opensOn, closesOn } = annualReviewWindow(fyStartYear);
  return isWithin(on, opensOn, closesOn);
}

/**
 * The performance-review 1-on-1 runs in the last quarter only (Jan-Mar).
 *
 * It is a wider window than the annual review itself on purpose: the conversation
 * has to happen *before* the final rating is settled in the last 15 days of March,
 * so booking it must open earlier than the rating does. A 1-on-1 held in August is
 * a different kind of meeting and is not what this system records.
 */
export function isOneOnOneWindowOpen(on: PlainDate = today()): boolean {
  return quarterIndexForDate(on) === 4;
}

/** The FY and quarter a date falls in - used to pick the default cycle. */
export function currentPeriod(on: PlainDate = today()): {
  fyStartYear: number;
  fyLabel: string;
  quarterIndex: QuarterIndex;
} {
  const fyStartYear = fiscalYearStartYear(on);
  return {
    fyStartYear,
    fyLabel: fiscalYearLabel(fyStartYear),
    quarterIndex: quarterIndexForDate(on),
  };
}
